import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Typography, alpha, useTheme,
} from '@mui/material';
import { Close, EditOutlined, LockOpenOutlined, LockOutlined, RestartAlt, Straighten } from '@mui/icons-material';
import { format } from 'date-fns';
import { examService } from '../../../services/examService';
import { PaperHistoryEntry, MarkChangeAction } from '../../../types/examination';
import { paperText } from './paperStatus';

export interface HistoryTarget {
  resultId: string;
  subjectId: string;
  /** e.g. "Ali Raza · Physics" */
  title: string;
}

const ACTIONS: Record<MarkChangeAction, { icon: React.ElementType; verb: string }> = {
  recorded: { icon: EditOutlined, verb: 'Entered' },
  cleared: { icon: RestartAlt, verb: 'Cleared' },
  locked: { icon: LockOutlined, verb: 'Submitted and locked' },
  unlocked: { icon: LockOpenOutlined, verb: 'Unlocked' },
  max_marks_changed: { icon: Straighten, verb: 'Maximum changed' },
};

const describe = (h: PaperHistoryEntry): string => {
  if (h.action === 'max_marks_changed') return `Out of ${h.from?.maxMarks} → out of ${h.to?.maxMarks}`;
  if (h.action === 'recorded' && h.to) {
    const to = paperText(h.to);
    return h.from && h.from.status !== 'PENDING' ? `${paperText(h.from)} → ${to}` : to;
  }
  if (h.action === 'cleared' && h.from) return `Was ${paperText(h.from)}`;
  return '';
};

/** Everything that happened to one paper, newest first: entries, corrections, locks and unlocks with their reasons. */
const PaperHistoryDialog: React.FC<{ target: HistoryTarget | null; onClose: () => void }> = ({ target, onClose }) => {
  const theme = useTheme();
  const [rows, setRows] = useState<PaperHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    examService.paperHistory(target.resultId, target.subjectId)
      .then(r => { if (!cancelled) setRows(r); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Could not load the history'); });
    return () => { cancelled = true; };
  }, [target]);

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>History</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{target?.title}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close"><Close fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 3 }}>
        {error ? <Alert severity="error">{error}</Alert>
          : !rows ? <LinearProgress />
          : rows.length === 0 ? <Typography variant="body2" color="text.secondary">Nothing has been entered for this paper yet.</Typography>
          : (
            <Stack spacing={0}>
              {rows.map((h, i) => {
                const { icon: Icon } = ACTIONS[h.action];
                const corrected = h.action === 'recorded' && !!h.from && h.from.status !== 'PENDING';
                const verb = corrected ? 'Changed' : ACTIONS[h.action].verb;
                const detail = describe(h);
                return (
                  <Stack key={h._id} direction="row" spacing={1.5} useFlexGap sx={{ position: 'relative', pb: i === rows.length - 1 ? 0 : 2 }}>
                    {i < rows.length - 1 && <Box sx={{ position: 'absolute', left: 13, top: 30, bottom: 2, width: '1px', bgcolor: 'divider' }} />}
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}>
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, pt: 0.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {verb}{detail && <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {detail}</Box>}
                      </Typography>
                      {h.reason && <Typography variant="body2" sx={{ mt: 0.25, fontStyle: 'italic' }}>“{h.reason}”</Typography>}
                      <Typography variant="caption" color="text.secondary">
                        {h.by?.name ?? 'Someone'} · {format(new Date(h.at), 'd MMM yyyy, h:mm a')}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}
      </DialogContent>
    </Dialog>
  );
};

export default PaperHistoryDialog;
